import { useEffect, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from './Header';
import { LinktreeLogo } from './LinktreeLogo';
import { cn } from '../utils/twUtils';
import useMediaQuery from '../utils/useMediaQuery';

interface BaseLayoutProps {
  heading?: ReactNode;
  subHeading?: ReactNode;
  headingClasses?: string;
  subHeadingClasses?: string;
  headerClasses?: string;
  mainClasses?: string;
  pageClasses?: string;
  contentWidth?: number | 'full';
  isMobileApp?: boolean;
  hideLogo?: boolean;
}

export interface DefaultLayoutProps extends BaseLayoutProps {
  variant?: 'default';
  children: ReactNode;
}

export interface InterstitialLayoutProps extends BaseLayoutProps {
  variant?: 'interstitial';
  children?: ReactNode;
}

export type BannerLayoutProps = BaseLayoutProps & {
  variant?: 'banner';
  bannerImageDesktop?: string;
  children?: ReactNode;
  backgroundColor?: string;
  mobileBannerOverlay?: boolean;
  bannerContent?: ReactNode;
  mobileLogoInForm?: boolean;
  /** Must be an absolutely positioned control with its own row insets. */
  mobileLogoContent?: ReactNode;
};

export type LayoutProps =
  DefaultLayoutProps | InterstitialLayoutProps | BannerLayoutProps;

interface ContentLayoutProps extends Omit<BaseLayoutProps, 'pageClasses'> {
  children?: ReactNode;
  mobileLogo?: ReactNode;
  mobileBannerOverlay?: boolean;
}

type DesktopBannerStatus = 'loading' | 'loaded' | 'not-needed';

declare global {
  interface Window {
    __ltDesktopBannerStatus?: DesktopBannerStatus;
  }
}

const DESKTOP_BANNER_MEDIA_QUERY = '(min-width: 1024px)';
const MOBILE_BANNER_CANVAS_CLASS = 'auth-mobile-banner-canvas';
export const MOBILE_LOGO_ROW_HEIGHT_PX = 15;

const publishDesktopBannerStatus = (status: DesktopBannerStatus): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.__ltDesktopBannerStatus = status;
  window.dispatchEvent(
    new CustomEvent('lt:desktop-banner-status', { detail: { status } })
  );
};

const useShouldRenderDesktopBanner = (enabled: boolean): boolean => {
  const matchesDesktopWidth = useMediaQuery(DESKTOP_BANNER_MEDIA_QUERY);

  return enabled && matchesDesktopWidth;
};

const ContentLayout = ({
  heading,
  subHeading,
  headingClasses,
  subHeadingClasses,
  headerClasses,
  mainClasses,
  contentWidth = 480,
  children,
  mobileLogo,
  mobileBannerOverlay = false,
}: ContentLayoutProps): ReactElement => {
  return (
    <>
      {mobileLogo}
      <Header
        heading={heading}
        subHeading={subHeading}
        headingClasses={headingClasses}
        subHeadingClasses={subHeadingClasses}
        className={cn(
          headerClasses,
          'mx-auto w-full max-w-[480px] px-6 md:px-0'
        )}
      />
      {children && (
        <main
          className={cn(
            mainClasses,
            'mx-auto w-full flex-grow px-6 pt-6 md:px-0',
            mobileBannerOverlay ? 'pb-12 lg:pb-[120px]' : 'pb-[120px]',
            contentWidth === 'full'
              ? 'max-w-[948px]'
              : `max-w-[${contentWidth}px]`
          )}
        >
          {children}
        </main>
      )}
    </>
  );
};

const Layout = (props: LayoutProps): ReactElement => {
  const { t } = useTranslation();
  const {
    variant = 'default',
    pageClasses,
    children,
    isMobileApp,
    hideLogo = false,
    ...contentProps
  } = props;
  const hasDesktopBanner =
    !isMobileApp &&
    variant === 'banner' &&
    Boolean(
      (props as BannerLayoutProps).bannerImageDesktop ||
      (props as BannerLayoutProps).bannerContent
    );
  const shouldRenderDesktopBanner =
    useShouldRenderDesktopBanner(hasDesktopBanner);
  const bannerContent = (props as BannerLayoutProps).bannerContent;
  const mobileBannerOverlay =
    variant === 'banner' &&
    Boolean((props as BannerLayoutProps).mobileBannerOverlay);

  useEffect(() => {
    const html = document.documentElement;

    if (!mobileBannerOverlay) {
      return;
    }

    html.classList.add(MOBILE_BANNER_CANVAS_CLASS);

    return () => {
      html.classList.remove(MOBILE_BANNER_CANVAS_CLASS);
    };
  }, [mobileBannerOverlay]);

  useEffect(() => {
    publishDesktopBannerStatus(
      hasDesktopBanner && shouldRenderDesktopBanner
        ? bannerContent
          ? 'loaded'
          : 'loading'
        : 'not-needed'
    );
  }, [bannerContent, hasDesktopBanner, shouldRenderDesktopBanner]);

  const LogoElement = (
    <div className="block h-[24px] lg:h-[32px] absolute top-6 start-6 lg:top-10 lg:start-10 z-50">
      {isMobileApp ? (
        <LinktreeLogo height="100%" />
      ) : (
        <a
          href="/"
          aria-label={t('Linktree home')}
          className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
        >
          <LinktreeLogo height="100%" />
        </a>
      )}
    </div>
  );
  // Handle banner variant
  if (variant === 'banner') {
    const {
      bannerImageDesktop,
      backgroundColor = 'bg-[#225AC0]',
      mobileBannerOverlay = false,
      bannerContent,
      mobileLogoInForm = false,
      mobileLogoContent,
    } = props as BannerLayoutProps;

    return (
      <div
        className={cn(
          pageClasses,
          ' relative flex w-full',
          'auth-viewport-height overflow-hidden',
          !isMobileApp ? 'lg:flex-row' : 'flex-col',
          mobileBannerOverlay && 'bg-white lg:bg-transparent'
        )}
      >
        {!hideLogo &&
          (mobileLogoInForm ? (
            <div className="hidden lg:block">{LogoElement}</div>
          ) : (
            LogoElement
          ))}
        {mobileBannerOverlay && (bannerContent || bannerImageDesktop) && (
          <div
            className="absolute inset-x-0 top-0 z-0 h-full w-full lg:hidden"
            data-testid="mobile-banner"
          >
            {bannerContent ?? (
              <img
                src={bannerImageDesktop}
                alt={t('Promotional Banner')}
                fetchPriority="high"
                className="h-auto w-full object-contain"
              />
            )}
          </div>
        )}
        <div
          className={cn(
            'flex w-full flex-shrink-0 flex-col',
            'overflow-y-auto',
            !isMobileApp ? 'lg:w-1/2' : 'w-full',
            mobileBannerOverlay &&
              'auth-viewport-height fixed inset-0 z-10 flex w-full flex-col overflow-y-auto overscroll-none lg:static lg:h-full lg:w-1/2',
            !mobileBannerOverlay && 'h-full'
          )}
        >
          {mobileBannerOverlay && (
            <div
              aria-hidden="true"
              className="new-auth-gradient-spacer flex-1 bg-gradient-to-b from-transparent to-white lg:hidden"
            />
          )}
          <div
            className={cn(
              mobileBannerOverlay
                ? 'shrink-0 bg-white lg:bg-transparent lg:pt-20'
                : 'pt-24 lg:pt-20',
              '2xl:pt-32',
              mobileBannerOverlay && 'new-auth-content-min-height'
            )}
          >
            <ContentLayout
              {...contentProps}
              mobileBannerOverlay={mobileBannerOverlay}
              mobileLogo={
                mobileLogoInForm ? (
                  <div className="grid grid-cols-[1fr_auto_1fr] items-start lg:hidden">
                    <div
                      className="relative min-w-0"
                      style={{ height: MOBILE_LOGO_ROW_HEIGHT_PX }}
                    >
                      {mobileLogoContent}
                    </div>
                    <a
                      href="/"
                      aria-label={t('Linktree home')}
                      className="-mx-[4.5px] -my-[4.5px] px-[4.5px] py-[4.5px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                    >
                      <LinktreeLogo height={`${MOBILE_LOGO_ROW_HEIGHT_PX}px`} />
                    </a>
                    <div aria-hidden="true" />
                  </div>
                ) : undefined
              }
            >
              {children}
            </ContentLayout>
          </div>
        </div>

        {shouldRenderDesktopBanner && (bannerContent || bannerImageDesktop) && (
          <div
            className={cn(
              'relative hidden overflow-hidden lg:block lg:h-full lg:w-1/2 lg:flex-shrink-0',
              backgroundColor
            )}
          >
            {bannerContent ? (
              bannerContent
            ) : (
              <img
                src={bannerImageDesktop}
                alt={t('Promotional Banner')}
                fetchPriority="high"
                data-lt-lcp-banner="true"
                onLoad={() => publishDesktopBannerStatus('loaded')}
                onError={() => publishDesktopBannerStatus('loaded')}
                className="h-full w-full object-cover"
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // Handle default and interstitial variants
  return (
    <div
      className={cn(
        pageClasses,
        ' flex min-h-screen w-full flex-col pt-[28px] lg:pt-32 2xl:pt-64',
        {
          'place-content-center lg:place-content-start':
            variant === 'interstitial',
        }
      )}
    >
      {!hideLogo && LogoElement}
      <ContentLayout {...contentProps}>{children}</ContentLayout>
    </div>
  );
};

export default Layout;
